import * as msal from "@azure/msal-browser";
import type { GraphUserProfile } from "@/shared/types";
import { acquireGraphTokenSilently, AuthError } from "@/features/auth/services/token-service";
import {
  isCacheValid,
  getCachedProfile,
  getCachedPhoto,
  cacheProfileAndPhoto,
} from "./graph-cache";

export { AuthError };

let ssoToken: string | null = null;

/**
 * Define um token SSO (Office.auth) para ser usado nas chamadas ao Graph.
 * Útil no Outlook mobile, onde `loginPopup` do MSAL é bloqueado pelo webview.
 *
 * @param token - Access token obtido via `Office.auth.getAccessToken()`.
 *
 * @example
 * setSsoToken(await Office.auth.getAccessToken());
 */
export function setSsoToken(token: string): void {
  ssoToken = token;
}

/**
 * Limpa o token SSO armazenado (ex.: ao fazer logout).
 *
 * @example
 * clearSsoToken();
 */
export function clearSsoToken(): void {
  ssoToken = null;
}

/**
 * Obtém token de acesso para o Graph: usa o token SSO (mobile) quando definido,
 * ou renova silenciosamente via MSAL através de {@link acquireGraphTokenSilently}.
 *
 * @param msalInstance - Instância MSAL autenticada.
 * @param forceRefresh - Se `true`, força renovação ignorando cache de access token.
 * @returns Access token válido.
 * @throws {@link AuthError} Se a aquisição silenciosa falhar.
 *
 * @example
 * const token = await getGraphToken(msalInstance);
 */
async function getGraphToken(
  msalInstance: msal.PublicClientApplication,
  forceRefresh: boolean = false
): Promise<string> {
  if (ssoToken) {
    return ssoToken;
  }

  const accounts = msalInstance.getAllAccounts();
  if (accounts.length === 0) {
    throw new AuthError("Nenhuma conta logada no MSAL.");
  }

  const result = await acquireGraphTokenSilently(msalInstance, accounts[0], forceRefresh);
  return result.accessToken;
}

/**
 * Busca o perfil do usuário logado via Microsoft Graph `/me`.
 *
 * @param msalInstance - Instância MSAL autenticada.
 * @returns Promise com o perfil do usuário do Graph.
 *
 * @example
 * ```typescript
 * const profile = await fetchUserProfile(msalInstance);
 * console.log(profile.displayName);
 * ```
 */
export async function fetchUserProfile(
  msalInstance: msal.PublicClientApplication
): Promise<GraphUserProfile> {
  const token = await getGraphToken(msalInstance);

  let response = await fetch(
    "https://graph.microsoft.com/v1.0/me?$select=givenName,surname,displayName,mail,jobTitle,businessPhones,mobilePhone,streetAddress,city,state,postalCode,country",
    { headers: { Authorization: `Bearer ${token}` } }
  );

  if (response.status === 401) {
    console.warn("[graph-api] /me retornou 401. Tentando refresh forcado do token...");
    try {
      const freshToken = await getGraphToken(msalInstance, true);
      response = await fetch(
        "https://graph.microsoft.com/v1.0/me?$select=givenName,surname,displayName,mail,jobTitle,businessPhones,mobilePhone,streetAddress,city,state,postalCode,country",
        { headers: { Authorization: `Bearer ${freshToken}` } }
      );
    } catch (tokenErr) {
      if (tokenErr instanceof AuthError) throw tokenErr;
      throw new Error(`Graph API /me falhou: ${response.status} ${response.statusText}`);
    }
  }

  if (!response.ok) {
    throw new Error(
      `Graph API /me falhou: ${response.status} ${response.statusText}`
    );
  }

  return (await response.json()) as GraphUserProfile;
}

/**
 * Busca a foto do usuário logado via Microsoft Graph `/me/photo/$value`.
 *
 * @param msalInstance - Instância MSAL autenticada.
 * @returns Promise com a foto em Base64 (Data URL) ou `null` se o usuário não tiver foto.
 *
 * @example
 * ```typescript
 * const photo = await fetchUserPhoto(msalInstance);
 * ```
 */
export async function fetchUserPhoto(
  msalInstance: msal.PublicClientApplication
): Promise<string | null> {
  const token = await getGraphToken(msalInstance);

  let response = await fetch(
    "https://graph.microsoft.com/v1.0/me/photo/$value",
    { headers: { Authorization: `Bearer ${token}` } }
  );

  if (response.status === 401) {
    console.warn("[graph-api] /me/photo retornou 401. Tentando refresh forcado do token...");
    try {
      const freshToken = await getGraphToken(msalInstance, true);
      response = await fetch(
        "https://graph.microsoft.com/v1.0/me/photo/$value",
        { headers: { Authorization: `Bearer ${freshToken}` } }
      );
    } catch (tokenErr) {
      if (tokenErr instanceof AuthError) throw tokenErr;
      if (response.status === 404) {
        console.warn("[graph-api] Usuário não possui foto no Microsoft 365.");
        return null;
      }
      throw new Error(
        `Graph API /me/photo falhou: ${response.status} ${response.statusText}`
      );
    }
  }

  if (!response.ok) {
    if (response.status === 404) {
      console.warn("[graph-api] Usuário não possui foto no Microsoft 365.");
      return null;
    }
    throw new Error(
      `Graph API /me/photo falhou: ${response.status} ${response.statusText}`
    );
  }

  const blob = await response.blob();
  return blobToBase64(blob);
}

function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

/**
 * Carrega perfil e foto do Graph com estratégia de cache: se o cache for válido,
 * retorna os dados cacheados; caso contrário, busca da API e atualiza o cache.
 * Em qualquer caso, uma falha na busca recorre ao cache (mesmo expirado).
 *
 * @param msalInstance - Instância MSAL autenticada.
 * @param forceRefresh - Se `true`, ignora o cache válido e busca sempre da API.
 * @returns Promise com o perfil e a foto do usuário.
 *
 * @example
 * ```typescript
 * const { profile, photo } = await loadProfileWithCache(msalInstance);
 * ```
 */
export async function loadProfileWithCache(
  msalInstance: msal.PublicClientApplication,
  forceRefresh: boolean = false
): Promise<{
  profile: GraphUserProfile;
  photo: string | null;
}> {
  if (!forceRefresh && isCacheValid()) {
    const cachedProfile = getCachedProfile();
    const cachedPhoto = getCachedPhoto();
    if (cachedProfile) {
      return { profile: cachedProfile, photo: cachedPhoto };
    }
  }

  try {
    const [profile, photo] = await Promise.all([
      fetchUserProfile(msalInstance),
      fetchUserPhoto(msalInstance),
    ]);
    cacheProfileAndPhoto(profile, photo);
    return { profile, photo };
  } catch (error) {
    console.error("[graph-api] Falha ao buscar da Graph API:", error);
    const cachedProfile = getCachedProfile();
    const cachedPhoto = getCachedPhoto();
    if (cachedProfile) {
      console.warn("[graph-api] Fallback: usando cache expirado.");
      return { profile: cachedProfile, photo: cachedPhoto };
    }
    throw error;
  }
}
